'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'

import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier'
import SettingsFormSectionDivider from '@/components/Settings/SettingsFormSectionDivider'
import Splash from '@/components/splash/Splash'
import { Button, Checkbox } from '@/components/UiElements'
import HelpTooltip from '@/components/UiElements/HelpTooltip'
import { settingsConfigHelp } from '@/components/Settings/configHelp'
import createProfanityCheckRule from '@/services/OrderService/createProfanityCheckRule'
import checkProfanityText from '@/services/OrderService/checkProfanityText'
import getProfanityCheckRule from '@/services/OrderService/getProfanityCheckRule'
import updateProfanityCheckRule from '@/services/OrderService/updateProfanityCheckRule'
import getSettings from '@/services/Settings/getSettings'
import upsertSetting from '@/services/Settings/upsertSetting'
import store from '@/store'
import { ProfanityCheckRule, ProfanityRuleStatus } from '@/types/profanity'
import { getErrorMessage } from '@/utils/errorMessages'
import notifyError from '@/utils/notifyError'

import ProfanityRuleRows, { profanityFieldClassName } from './ProfanityRuleRows'
import ProfanityThresholdBar from './ProfanityThresholdBar'
import {
  createDefaultProfanityEditorState,
  DEFAULT_PROFANITY_MAX_SCORE,
  mapProfanityEditorStateToRules,
  mapProfanityPolicyToEditorState,
  ProfanityEditorState,
  PROFANITY_STATUS_BADGE_CLASS,
  updateProfanityThreshold,
} from './profanityRuleUtils'

type LoadState = 'loading' | 'ready' | 'error'

const TEXT_PROFANITY_SETTING_KEY = 'text_profanity'

// Setting values can come back as real booleans or as their string form ("true"/"false").
const parseBooleanSetting = (value: unknown): boolean => value === true || value === 'true'

type ProfanityRulesFormProps = {
  onCancel: () => void
  onSaved: () => void
}

const ProfanityRulesForm = ({ onCancel, onSaved }: ProfanityRulesFormProps) => {
  const { currentUser } = useSnapshot(store)
  const companyId = currentUser?.selectedCompany?.id ?? null
  const storeName = currentUser?.selectedCompany?.store_name ?? ''
  const loadRequestId = useRef(0)

  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [ruleExists, setRuleExists] = useState(false)
  const [editorState, setEditorState] = useState<ProfanityEditorState>(createDefaultProfanityEditorState)
  const [textProfanityEnabled, setTextProfanityEnabled] = useState(true)
  const [testText, setTestText] = useState('')
  const [testResult, setTestResult] = useState<string | null>(null)
  const rules = mapProfanityEditorStateToRules(editorState)

  const loadRules = useCallback(async () => {
    const requestId = ++loadRequestId.current
    setLoadState('loading')

    try {
      const [policy, settings] = await Promise.all([getProfanityCheckRule(), getSettings()])
      if (requestId !== loadRequestId.current) return

      setEditorState(mapProfanityPolicyToEditorState(policy))
      setRuleExists(Boolean(policy?.rules?.length))

      const textProfanitySetting = settings.find((setting) => setting.key === TEXT_PROFANITY_SETTING_KEY)
      // Default to enabled when the setting has never been saved.
      setTextProfanityEnabled(textProfanitySetting ? parseBooleanSetting(textProfanitySetting.value) : true)
      setLoadState('ready')
    } catch (error) {
      if (requestId !== loadRequestId.current) return

      setLoadState('error')
      notifyError(error, 'Unable to load profanity rules.')
    }
  }, [])

  useEffect(() => {
    void loadRules()

    return () => {
      loadRequestId.current += 1
    }
  }, [companyId, loadRules])

  const handleActiveStatusChange = (status: ProfanityRuleStatus, active: boolean) => {
    setEditorState((current) => {
      const activeCount = Object.values(current.activeStatuses).filter(Boolean).length
      if (!active && current.activeStatuses[status] && activeCount === 1) return current

      return {
        ...current,
        activeStatuses: {
          ...current.activeStatuses,
          [status]: active,
        },
      }
    })
  }

  const handleThresholdChange = (status: ProfanityRuleStatus, value: number) => {
    const { state, committedValue } = updateProfanityThreshold(editorState, status, value)
    setEditorState(state)
    return committedValue
  }

  const handleReset = () => setEditorState(createDefaultProfanityEditorState())

  const handleSave = async () => {
    if (loadState !== 'ready' || isSaving) return
    if (!companyId) {
      notifyError('No company selected.')
      return
    }

    const payload: ProfanityCheckRule = {
      company_id: companyId,
      max_score: DEFAULT_PROFANITY_MAX_SCORE,
      rules,
    }
    const notificationId = startNotif('Saving profanity rules...')
    setIsSaving(true)
    let saved = false

    try {
      if (ruleExists) {
        await updateProfanityCheckRule(payload)
      } else {
        await createProfanityCheckRule(payload)
      }
      await upsertSetting({ key: TEXT_PROFANITY_SETTING_KEY, value: textProfanityEnabled })
      setRuleExists(true)
      closeNotif(notificationId, 'Profanity rules saved successfully.')
      saved = true
    } catch (error) {
      closeNotif(notificationId, getErrorMessage(error, 'Failed to save profanity rules.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }

    if (saved) onSaved()
  }

  const handleTest = async () => {
    if (isTesting) return
    if (!testText.trim()) {
      notifyError('Enter text to check.')
      return
    }
    if (!storeName) {
      notifyError('No store is available for the selected company.')
      return
    }

    const notificationId = startNotif('Checking text...')
    setIsTesting(true)
    setTestResult(null)

    try {
      const status = await checkProfanityText(testText.trim(), storeName)
      if (!status) throw new Error('The profanity check returned no result.')

      setTestResult(status)
      closeNotif(notificationId, 'Text checked successfully.')
    } catch (error) {
      closeNotif(notificationId, getErrorMessage(error, 'Unable to check this text.'), { type: 'error' })
    } finally {
      setIsTesting(false)
    }
  }

  const isBusy = loadState === 'loading' || isSaving || isTesting
  const busyText = loadState === 'loading'
    ? 'Loading profanity rules...'
    : isSaving
      ? 'Saving profanity rules...'
      : 'Checking text...'

  return (
    <div className="flex flex-col gap-xxxl">
      {isBusy && <Splash text={busyText} />}

      {loadState === 'error' && (
        <div className="flex min-h-[220px] w-[850px] max-w-[calc(100vw-80px)] flex-col items-center justify-center gap-md rounded-md bg-gray-50 p-xl">
          <p className="text-text-md text-gray-400">Profanity rules could not be loaded.</p>
          <Button type="alt" onClick={() => void loadRules()}>
            Retry
          </Button>
        </div>
      )}

      {loadState !== 'error' && (
        <div className="grid w-[850px] max-w-[calc(100vw-80px)] grid-cols-12 gap-md">
          <section className="col-span-12 grid grid-cols-12 gap-md">
            <SettingsFormSectionDivider
              className="col-span-12"
              title="1. Images Score Rules"
              description="Each image is scored from 0% (no profanity) to 100% (profanity). Assign each score range to an outcome — the active ranges always cover the full 0% to 100% scale."
            />

            <div className="col-span-12 flex flex-col gap-xl rounded-md bg-gray-100 p-md">
              <ProfanityRuleRows
                state={editorState}
                rules={rules}
                disabled={isBusy}
                onActiveChange={handleActiveStatusChange}
                onThresholdChange={handleThresholdChange}
              />

              <ProfanityThresholdBar
                rules={rules}
                disabled={isBusy}
                onThresholdChange={handleThresholdChange}
              />

              <div>
                <Button type="link" onClick={handleReset} disabled={isBusy}>
                  Reset to Default
                </Button>
              </div>
            </div>
          </section>

          <section className="col-span-12 grid grid-cols-12 gap-md pt-xl">
            <SettingsFormSectionDivider
              className="col-span-12"
              title="2. Text"
              description="Check a phrase against the profanity policy for the selected store."
            />

            <HelpTooltip help={settingsConfigHelp.textProfanity} title="Enable text profanity check">
              <div className="col-span-12 flex items-center gap-sm rounded-md bg-gray-100 p-md">
                <Checkbox
                  label="Enable text profanity check"
                  defaultChecked={textProfanityEnabled}
                  disabled={isBusy}
                  onChange={setTextProfanityEnabled}
                />
              </div>
            </HelpTooltip>

            <HelpTooltip help={settingsConfigHelp.testProfanityText} title="Test profanity text">
              <div className="col-span-12 grid grid-cols-12 gap-md">
                <input
                  aria-label="Text to check"
                  type="text"
                  value={testText}
                  disabled={isBusy}
                  placeholder="Type text to check..."
                  onChange={(event) => setTestText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void handleTest()
                  }}
                  className={`${profanityFieldClassName} col-span-9 w-full`}
                />
                <div className="col-span-3 flex">
                  <Button type="alt" className="w-full" onClick={() => void handleTest()} disabled={isBusy}>
                    Test
                  </Button>
                </div>
              </div>
            </HelpTooltip>
            {testResult && (
              <div className="col-span-12 flex items-center gap-sm">
                <span className="text-text-md text-gray-400">Result:</span>
                <span
                  className={`rounded-md px-sm py-[2px] text-title-sm capitalize ${
                    PROFANITY_STATUS_BADGE_CLASS[testResult] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {testResult.replace(/_/g, ' ')}
                </span>
              </div>
            )}
          </section>
        </div>
      )}

      <div className="flex justify-end gap-md">
        <Button size="lg" type="alt" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button size="lg" onClick={() => void handleSave()} disabled={loadState !== 'ready' || isBusy}>
          Save
        </Button>
      </div>
    </div>
  )
}

export default ProfanityRulesForm
